import { Button, Flex, message, Modal, Space, Typography } from "antd";
import { FilePdfFilled, FilePdfOutlined } from "@ant-design/icons";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { MasterConfigs } from "../../../interfaces/entity";
import FormSetup from "../../../components/Forms/FormSetup";
import { useDispatch } from "react-redux";
import { getData, uploadFile } from "../../../actions/apiCall";
import SignatureUpload, { SignatureUploadRef } from "../../../components/SignatureUpload";

const { Text } = Typography;

interface Props {
    onLoadingChange?: (loading: boolean) => void;
}

const Agreements = forwardRef((props: Props, ref) => {
    const { onLoadingChange } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    const [isSign, setSign] = React.useState(false);
    const [formData, setFormData] = React.useState<FormData | null>(null);

    const [loading, setLoading] = React.useState<boolean>(false);
    const submitPromiseRef = React.useRef<{ resolve?: (value: any) => void; reject?: (reason?: any) => void }>({});
    const [vendorId, setVendorId] = React.useState<string>('');
    const [agreementData, setAgreementData] = React.useState<any>({});
    const [fileType, setFileType] = React.useState<string>('msa');
    const dispatch = useDispatch();
    const signatureRef = useRef<SignatureUploadRef>(null);
    const [formId, setFormId] = React.useState<string>('');

    const getQueryStep = () => {
        const params = new URLSearchParams(location.search);
        const vendorId = params.get('vendor')
        setVendorId(vendorId || '')
        return vendorId
    };

    useEffect(() => {
        getQueryStep()
    }, [location.search]);

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    useImperativeHandle(ref, () => ({
        submitStep: () => {
            return new Promise((resolve, reject) => {
                submitPromiseRef.current = { resolve, reject };
            });
        }
    }));


    useEffect(() => {
        if (vendorId) {
            getDoccuments()
        }
    }, [vendorId]);


    const getDoccuments = async () => {
        try {
            const resData = await dispatch(getData(`/v1/vendor/agreements/${vendorId}`));
            if (resData) {
                setAgreementData(resData.data)
            } else {
                setAgreementData(null)
            }
        } catch (error) {
            console.error('Error fetching document:', error);
        }
    }

    const onSubmit = async (values: any) => {
        try {
            if (!formData) {
                message.error('Please upload signature');
                return { code: 400, message: 'Please upload signature' }
            }
            formData?.append('authorizedSignatoryName', values.authorizedSignatoryName);
            formData?.append('authorizedSignatoryDesignation', values.authorizedSignatoryDesignation);
            formData?.append('fileType', fileType);
            const resData = await uploadFile(`/v1/vendor/agreements/${vendorId}`, formData);
            if (resData) {
                signatureRef.current?.reset();
                message.success('Document signed successfully');
                setSign(false);
                setLoading(false);
                return { code: 200 }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            return { code: 400, message: 'Please upload signature' }
        } finally {
            setLoading(false);
        }
    }

    const config: MasterConfigs = {
        api: () => '/v1/vendor',
        title: 'Vendor NDA',
        modalTitle: () => 'Vendor NDA',
        onSubmit: async (id, values: any) => {
            setLoading(true);
            return onSubmit(values)
        },
        onRefresh: () => {
            signatureRef.current?.reset();
            getDoccuments()
        },
        getFormColumns: () => [
            {
                label: 'Company Name',
                dataIndex: 'vendorName',
                name: 'vendorName',
                type: 'text',
                inputProps: {
                    disabled: true,
                },
            },
            {
                label: 'Authorized Signatory Name',
                dataIndex: 'authorizedSignatoryName',
                name: 'authorizedSignatoryName',
                type: 'text',
                rules: [
                    {
                        required: true,
                        message: 'Please enter Authorized Signatory Name',
                    },
                ],
            },
            {
                type: 'custom',
                span: 8,
            },
            {
                label: 'Authorized Signatory Designation',
                dataIndex: 'authorizedSignatoryDesignation',
                name: 'authorizedSignatoryDesignation',
                type: 'text',
                rules: [
                    {
                        required: true,
                        message: 'Please enter Authorized Signatory Designation',
                    },
                ],
            },
            {
                type: 'custom',
                span: 24,
                formItem: () => <>
                    <Text>
                        <Text type="danger">*</Text> Upload Signature
                    </Text>
                    <SignatureUpload key={fileType} onChange={handleSignatureChange} />
                </>
            },
            {
                type: 'custom',
                span: 24,
                formItem: () => <>
                    <Text>
                        Note : We are not storing your signature. It is only used for this document.
                    </Text>
                </>
            }
        ]
    }

    const handleSignatureChange = (data: FormData) => {
        setFormData(data);
    };

    useEffect(() => {
        if (formId && fileType) {
            setSign(true);
        }
    }, [formId, fileType]);

    return (
        <div className="">
            <FormSetup config={config} isModal={true} open={isSign} onClose={() => {
                setSign(false);
                setFormId('');
                setFileType('');
            }} id={formId} />
            <Modal
                open={isOpen}
                title="MSA Document"
                onCancel={() => setIsOpen(false)}
                footer={null}
                width={1000}
                maskClosable={false}
            >
                <iframe src={`${process.env.FILE_URL}/files/agreements/${agreementData[fileType]}`} width={'100%'} />
            </Modal>
            <div className="">
                <div className="p-4 flex justify-between items-center bg-white border border-gray-300 rounded-md mb-4">
                    <Flex align="center" gap={4} justify="start">
                        <FilePdfOutlined className="text-md text-gray-500" />
                        MSA Doccument
                    </Flex>
                    <Space direction="horizontal">
                        <Button type="link" onClick={() => {
                            setFileType('msa')
                            setIsOpen(true)
                        }}>View</Button>
                        <Button type="link" onClick={() => {
                            setFormId(vendorId)
                            setFileType('msa')
                        }} disabled={agreementData?.msaSigned}>{agreementData?.msaSigned ? 'Signed' : 'Sign'}</Button>
                    </Space>
                </div>
                <div className="p-4 flex justify-between items-center bg-white border border-gray-300 rounded-md mb-4">
                    <Flex align="center" gap={4} justify="start">
                        <FilePdfOutlined className="text-md text-gray-500" />
                        NDA Doccument
                    </Flex>
                    <Space direction="horizontal">
                        <Button type="link" onClick={() => {
                            setFileType('nda')
                            setIsOpen(true)
                        }}>View</Button>
                        <Button type="link" onClick={() => {
                            setFormId(vendorId)
                            setFileType('nda')
                        }} disabled={agreementData?.ndaSigned}>{agreementData?.ndaSigned ? 'Signed' : 'Sign'}</Button>
                    </Space>
                </div>
            </div>
        </div>
    );
})

export default Agreements;